import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { AlertTriangle, CheckCircle2, Download, FileArchive, FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, czk, errMsg } from "../lib/api";
import { Card, PageTitle } from "./ui";

const MONTHS = ["leden", "únor", "březen", "duben", "květen", "červen", "červenec", "srpen", "září", "říjen", "listopad", "prosinec"];
const label = (m) => `${MONTHS[Number(m.slice(5)) - 1]} ${m.slice(0, 4)}`;
const lastMonths = () => [...Array(12)].map((_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
const METHOD_COLORS = { comgate: "#7A8460", qr: "#F2C230", stripe: "#D96B43", voucher: "#D9A3A0", free: "#A3AC86" };

const save = async (url, name, setBusy) => {
  setBusy(name);
  try {
    const r = await adminApi.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    toast.error(errMsg(e));
  } finally {
    setBusy("");
  }
};

const Stat = ({ k, v, sub, accent, i }) => (
  <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
    className={`rounded-3xl p-5 ${accent ? "bg-[#7A8460] text-white" : "border bg-white"}`} data-testid={`acc-stat-${i}`}>
    <p className={`text-xs font-semibold uppercase tracking-wider ${accent ? "text-white/80" : "text-[#6E7565]"}`}>{k}</p>
    <p className="mt-2 font-serif text-2xl font-black sm:text-3xl">{v}</p>
    {sub && <p className={`mt-1 text-xs ${accent ? "text-white/80" : "text-[#6E7565]"}`}>{sub}</p>}
  </motion.div>
);

const DownloadBar = ({ month, busy, setBusy }) => {
  const files = [["zip", `ucetnictvi-${month}.zip`], ["xlsx", `prehled-${month}.xlsx`], ["pdf", `prehled-${month}.pdf`]];
  const spin = (n) => busy === n && <Loader2 className="h-4 w-4 animate-spin" />;
  return (
    <div className="flex flex-wrap gap-2">
      <button className="btn-sage" disabled={!!busy} onClick={() => save(`/accounting/${month}/zip`, files[0][1], setBusy)} data-testid="acc-download-zip">
        {spin(files[0][1]) || <FileArchive className="h-4 w-4" />} Stáhnout vše pro účetní (ZIP)
      </button>
      <button className="btn-ghost" disabled={!!busy} onClick={() => save(`/accounting/${month}/xlsx`, files[1][1], setBusy)} data-testid="acc-download-xlsx">{spin(files[1][1]) || <FileSpreadsheet className="h-4 w-4" />} Excel</button>
      <button className="btn-ghost" disabled={!!busy} onClick={() => save(`/accounting/${month}/pdf`, files[2][1], setBusy)} data-testid="acc-download-pdf">{spin(files[2][1]) || <FileText className="h-4 w-4" />} PDF</button>
    </div>
  );
};

const Methods = ({ s }) => (
  <Card title="Podle způsobu platby">
    <div className="space-y-3">
      {s.by_method.map((m) => (
        <div key={m.method} data-testid={`acc-method-${m.method}`}>
          <div className="mb-1 flex justify-between text-sm"><span>{m.label} <span className="text-[#6E7565]">· {m.count}×</span></span><b>{czk(m.amount)}</b></div>
          <div className="h-2.5 rounded-full bg-[#F2EEE4]"><div className="h-full rounded-full" style={{ width: `${Math.max(3, (m.amount / (s.received || 1)) * 100)}%`, background: METHOD_COLORS[m.method] || "#7A8460" }} /></div>
        </div>
      ))}
      {!s.by_method.length && <p className="text-sm text-[#6E7565]">Žádné platby v tomto měsíci.</p>}
    </div>
    <div className="mt-5 grid grid-cols-2 gap-3 border-t pt-4 text-sm">
      <p><span className="block text-xs text-[#6E7565]">Uhrazeno poukazy</span>{czk(s.vouchers_redeemed)}</p>
      <p><span className="block text-xs text-[#6E7565]">Poskytnuté slevy</span>{czk(s.discounts)}</p>
      <p><span className="block text-xs text-[#6E7565]">Prodané poukazy</span>{czk(s.vouchers_sold)}</p>
      <p><span className="block text-xs text-[#6E7565]">Faktury</span>{s.invoices} / {s.orders}</p>
    </div>
  </Card>
);

const Products = ({ s }) => (
  <Card title="Prodané produkty">
    <div className="max-h-72 divide-y overflow-y-auto text-sm">
      {s.products.map((p) => <div key={p.name} className="flex gap-3 py-2"><span className="flex-1">{p.name}</span><span className="text-[#6E7565]">{p.qty} ks</span><b className="w-24 text-right">{czk(p.amount)}</b></div>)}
      {!s.products.length && <p className="py-2 text-[#6E7565]">Nic neprodáno.</p>}
    </div>
  </Card>
);

const OrdersTable = ({ s, busy, setBusy }) => (
  <Card title={`Objednávky a faktury (${s.rows.length})`} action={s.missing_invoices.length ? <span className="flex items-center gap-1 rounded-full bg-[#F8E1D7] px-3 py-1 text-xs text-[#B4532F]"><AlertTriangle className="h-3.5 w-3.5" /> {s.missing_invoices.length} bez faktury</span> : s.rows.length > 0 && <span className="flex items-center gap-1 rounded-full bg-[#E9EFE0] px-3 py-1 text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> vše spárováno</span>}>
    <div className="-mx-2 overflow-x-auto">
      <table className="w-full min-w-[820px] text-left text-sm" data-testid="acc-orders-table">
        <thead className="text-xs uppercase tracking-wider text-[#6E7565]"><tr>{["Zaplaceno", "Objednávka", "Faktura", "Zákazník", "Platba", "Ks", "Sleva / poukaz", "Přijato", ""].map((h) => <th key={h} className="px-2 py-2 font-semibold">{h}</th>)}</tr></thead>
        <tbody className="divide-y">
          {s.rows.map((r) => (
            <tr key={r.id} className="hover:bg-[#FBF8F1]" data-testid="acc-order-row">
              <td className="px-2 py-2.5">{r.date}</td><td className="px-2 font-mono text-xs">{r.number}</td>
              <td className="px-2">{r.invoice_number ? <span className="font-mono text-xs">{r.invoice_number}</span> : <span className="rounded-full bg-[#F8E1D7] px-2 text-xs text-[#B4532F]">chybí</span>}</td>
              <td className="max-w-[200px] truncate px-2" title={r.email}>{r.customer}</td>
              <td className="px-2"><span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: METHOD_COLORS[r.method] }} />{r.method_label}</span></td>
              <td className="px-2">{r.qty}</td>
              <td className="px-2 text-xs text-[#D96B43]">{r.discount ? `-${czk(r.discount)}` : ""}{r.voucher_amount ? ` poukaz -${czk(r.voucher_amount)}` : ""}</td>
              <td className="px-2 font-semibold">{czk(r.total)}</td>
              <td className="px-2">{r.invoice_number && <button title="Stáhnout fakturu" disabled={!!busy} onClick={() => save(`/orders/${r.id}/invoice`, `${r.invoice_number}.pdf`, setBusy)} className="rounded-full p-1.5 hover:bg-[#E9EFE0]" data-testid={`acc-invoice-${r.number}`}><Download className="h-4 w-4" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!s.rows.length && <p className="p-6 text-center text-sm text-[#6E7565]" data-testid="acc-empty">V tomto měsíci nebyla zaplacena žádná objednávka.</p>}
    </div>
  </Card>
);

export default function Accounting() {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(lastMonths()[0]);
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { adminApi.get("/accounting/months").then((r) => setMonths(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setS(null);
    adminApi.get(`/accounting/${month}`).then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month]);
  const all = useMemo(() => [...new Set([...lastMonths(), ...months.map((m) => m.month)])].sort().reverse(), [months]);
  const byMonth = Object.fromEntries(months.map((m) => [m.month, m]));
  const chart = lastMonths().reverse().map((m) => ({ m, name: MONTHS[Number(m.slice(5)) - 1].slice(0, 3), received: byMonth[m]?.received || 0 }));
  return (
    <div data-testid="admin-accounting">
      <PageTitle title="Účetnictví" sub="Měsíční přehledy, faktury a balíček pro účetní jedním kliknutím" />
      <div className="no-scrollbar mb-6 flex gap-2 overflow-x-auto pb-1">
        {all.map((m) => (
          <button key={m} onClick={() => setMonth(m)} data-testid={`acc-month-${m}`}
            className={`shrink-0 rounded-2xl border px-4 py-2 text-left text-sm transition-colors ${m === month ? "border-[#7A8460] bg-[#7A8460] text-white" : "bg-white hover:bg-[#E9EFE0]"}`}>
            <b className="block capitalize">{label(m)}</b><span className="text-xs opacity-80">{byMonth[m] ? `${byMonth[m].orders} obj. · ${czk(byMonth[m].received)}` : "bez tržeb"}</span>
          </button>
        ))}
      </div>
      <div className="mb-6 flex flex-col gap-4 rounded-[28px] bg-[#E9EFE0] p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <div><p className="font-script text-2xl text-[#D96B43]">Přehled za</p><h2 className="h-display text-3xl capitalize sm:text-4xl" data-testid="acc-month-title">{label(month)}</h2></div>
        <DownloadBar month={month} busy={busy} setBusy={setBusy} />
      </div>
      {!s ? <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#7A8460]" /> : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Stat i={0} accent k="Přijaté peníze" v={czk(s.received)} sub="skutečně zaplaceno" />
            <Stat i={1} k="Celkové příjmy" v={czk(s.revenue)} sub="tržby po slevách vč. poukazů" />
            <Stat i={2} k="Objednávky" v={s.orders} sub="zaplacené v měsíci" />
            <Stat i={3} k="Prodané produkty" v={`${s.items_sold} ks`} sub={`${s.products.length} různých`} />
            <Stat i={4} k="Faktury" v={`${s.invoices} / ${s.orders}`} sub={s.missing_invoices.length ? "některé chybí" : "vše spárováno"} />
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            <Card title="Posledních 12 měsíců" className="lg:col-span-1">
              <div className="h-56" data-testid="acc-chart"><ResponsiveContainer><BarChart data={chart}><XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} /><Tooltip formatter={(v) => czk(v)} cursor={{ fill: "#F2EEE4" }} />
                <Bar dataKey="received" name="Přijato" radius={[8, 8, 0, 0]} fill="#A3AC86" onClick={(d) => setMonth(d.m)} /></BarChart></ResponsiveContainer></div>
            </Card>
            <Methods s={s} />
            <Products s={s} />
          </div>
          <OrdersTable s={s} busy={busy} setBusy={setBusy} />
        </div>
      )}
    </div>
  );
}
